/* eslint-disable react/prop-types */
import * as eventStyles from '../styles/events/event-page.module.scss';
import * as pageStyles from '../styles/internal-page/internal-page.module.scss';
import * as responsiveStyles from '../styles/responsive.module.scss';
import {GatsbyImage, getImage} from 'gatsby-plugin-image';
import {getImageAndAlt,
  getSEOInformations,
  mapSectionIntoMain,
  removeUselessWidgets
} from '../utils/parsing';

import BottomLayout from '../components/layout/templates/BottomLayout';
import Content from '../components/page_structure/Content';
import EventLayout from '../components/layout/templates/EventLayout';
import MarketingButton from '../components/page_structure/MarketingButton';
import React from 'react';
import RelatedContent from '../components/page_structure/RelatedContent';
import Seo from '../seo/seo';
import Title from '../components/shared/Title';
import clsx from 'clsx';
import {graphql, Slice} from 'gatsby';
import {useTranslation, getI18n} from 'react-i18next';
import {altNN} from '../utils/parsing';
import '../services/i18n';
import { setupi18n } from '../utils/utils';

const getTextLanguageAttr = (locale, evt) => {
  const llw = locale ? locale.toLowerCase() : null;
  const elw = evt?.evt_language ? evt.evt_language.toLowerCase() : null;
  if (llw && elw && llw !== elw) {
    return elw;
  }
  return null;
};

export const Head = ({ data, pageContext, location }) => {
  const event = data.page;

  const {locale, generalSeo, websiteSeo, name } = pageContext;
  const {
    canonical_link,
    pageSeo,
    metaDescription,
  } = getSEOInformations(data);
  const cover = getImageAndAlt(event.cover, locale);
  return (
    <Seo
      canonicalLink={canonical_link}
      coverMedia={cover}
      end_date={event.end_date}
      eventLive={data.live}
      eventLocation={event?.event_location}
      eventOrganizer={data.organizer}
      generalSeo={generalSeo}
      language={locale}
      location={location}
      metaDescription={metaDescription}
      pageSeo={pageSeo}
      start_date={event.start_date}
      type={pageContext.pageType}
      website={name}
      websiteSeo={websiteSeo}
    />
  );
};

export default function EventPage ({data, pageContext}) {
  const {websiteRoot, breadcrumbs, locale} = pageContext;
  setupi18n(locale, getI18n());
  const event = data.page;
  const txtLangAttr = getTextLanguageAttr(locale, event);

  const targetObserver = React.useRef(null);

  const cover = getImageAndAlt(event.cover, locale);
  const displayCover = event?.show_cover_media;
  const [displayRelatedContent, setDisplayRelatedContent] = React.useState(false);

  const callBackObserver = (entries) => {
    if (window && window.innerWidth < 1280) {
      setDisplayRelatedContent(!entries[0].isIntersecting);
    } else {
      setDisplayRelatedContent(false);
    }
  };

  React.useEffect(() => {
    const options = {
      root: document.querySelector('#scrollArea'),
      rootMargin: '100px 0px 0px 0px',
      threshold: 1.0
    };

    const observer = new IntersectionObserver(callBackObserver, options);
    if (targetObserver.current)
      observer.observe(targetObserver.current);
  }, []);

  const {t} = useTranslation();

  const [newContent] = React.useState(removeUselessWidgets(mapSectionIntoMain(event?.content)));
  const [newBottom] = React.useState(removeUselessWidgets(mapSectionIntoMain(event?.bottom)));

  const getEventRelatedContents = (event) => {
    const arr = [];
    if (event?.registration?.length > 0) {
      event.registration.forEach((reglink) => {
        const elem = {__typename: 'CmsPortalComponentWidgetActionElement'};

        if (reglink?.type === 'EXTERNAL') {
          elem.label = t('event.page.inscription.external');
          elem.actionColor = 'RED';
        } else if (reglink?.type === 'BOTH') {
          elem.label = t('event.page.inscription.both');
          elem.actionColor = 'BLACK';
        } else if (reglink?.type === 'INTERNAL') {
          elem.label = t('event.page.inscription.internal');
          elem.actionColor = 'BLACK';
        }
        elem.URL = reglink?.URL;
        arr.push(elem);
      });
    }
    if (event?.event_location || event?.entities || event?.start_date || event?.end_date) {
      const elem = {__typename: 'CmsPortalComponentWidgetEventInfo'};
      elem.title = t('event.page.widget');
      elem.event = event;
      arr.push(elem);
    }
    return arr;
  };

  const [relatedContents] = React.useState(getEventRelatedContents(event));

  const innerTemplate = (
    <div className={eventStyles.RootDiv}>
      <div className={clsx(pageStyles.marginBetweenBlocs)} >
        <div className={clsx(pageStyles.marginBetweenBlocs, eventStyles.contentHeader)}>
          {cover && displayCover && (
            <div className={eventStyles.blocCover}>
              <div className={eventStyles.innerBloc}>
                <GatsbyImage
                  alt={altNN(cover.alt)}
                  className={pageStyles.cover}
                  image={getImage(cover.image)}
                  loading="lazy"
                />
              </div>
            </div>
          )}
          {event?.title && cover && displayCover && (
            <div className={eventStyles.divTitre}>
              <div className={eventStyles.blocTitre}>
                <div className={eventStyles.innerBlocTitre}>
                  <Title
                    color="RED"
                    date={event.start_date}
                    label={event.title}
                    lang={txtLangAttr}
                    size={35}
                  />
                </div>
              </div>
            </div>
          )}
          {event?.title && (!cover || !displayCover) && (
            <Title
              color="RED"
              date={event.start_date}
              label={event.title}
              lang={txtLangAttr}
              separator
              size={35}
            />
          )}
          <div ref={targetObserver}/>
          <div
            className={clsx(responsiveStyles.smartphone, {
              [eventStyles.espacement]: (event?.title && cover && displayCover),
              [pageStyles.marginBetweenBlocs]:!(event?.title && cover && displayCover)
            })}
            style={{marginTop: 10}}
          >
            {' '}
            {relatedContents?.map((elem, index) => {
              return (
                <RelatedContent elem={elem}
                  key={index}
                />
              );
            })}
          </div>
          <div
            className={pageStyles.marginBetweenBlocs}
            style={{width: 'inherit'}}
          >
            {newContent?.map((content, index) => {
              return (
                <Content
                  elem={content}
                  fullWidth={relatedContents?.length > 0 ? false : true}
                  key={'content-smartphone-' + index}
                  lang={txtLangAttr}
                  locale={locale}
                  siteURL={websiteRoot}
                />
              );
            })}
          </div>
          {displayCover && cover && (
            <p
              className={eventStyles.author}
            >
              {cover.legend}
            </p>
          )}
        </div>
      </div>
      {displayRelatedContent && relatedContents?.length > 0 && (
        <MarketingButton
          actionElement={relatedContents[0]}
          mobile
          secondActionElement={relatedContents.length > 1
            && relatedContents[1].__typename === 'CmsPortalComponentWidgetActionElement' ? relatedContents[1] : null}
        />)}
      {relatedContents?.length > 0 && (
        <div className={eventStyles.relatedContentDesktop }>
          <div style={{minWidth: '16%'}}></div>
          <div style={{maxWidth: '84%'}}>
            <div
              className={eventStyles.contentRelated}
            >
              {relatedContents?.map((elem, index) => {
                return (
                  <RelatedContent elem={elem}
                    key={index}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>);

  return (
    <Slice alias={'internal-page-layout'}>
      <EventLayout
        filAriane={breadcrumbs}
        lang={txtLangAttr}
        url={websiteRoot}
      >
        {innerTemplate}
      </EventLayout>
      <div className={clsx(pageStyles.marginBetweenBlocs, eventStyles.contentBottomLayout)}>
        <BottomLayout>
          {newBottom?.map((content, index) => {
            return (
              <Content
                elem={content}
                fullWidth
                key={'bottom-' + index}
                lang={txtLangAttr}
                locale={locale}
                siteURL={websiteRoot}
              />
            );
          })}
        </BottomLayout>
      </div>
    </Slice>);
}

export const query = graphql`
  query ($eventId: String!) {
    page: cmsPortalEvent(id: { eq: $eventId }) {
      ...EventData
      content {
        ...EventContent
      }
      related_contents {
        ...RelatedContentsFragment
      }
      bottom {
        ...EventContent
      }
    }
  }
`;
